import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { ContactEntry } from '../../../api/companyProfileApi';

export const CATEGORY_OPTIONS = [
  { value: 'core-policies', label: 'Core Policies' },
  { value: 'compliance-regulatory', label: 'Compliance & Regulatory' },
  { value: 'risk-assessments', label: 'Risk & Assessments' },
  { value: 'data-governance', label: 'Data Governance' },
  { value: 'ai-system-documentation', label: 'AI System Documentation' },
  { value: 'incident-response', label: 'Incident & Response' },
  { value: 'contracts-agreements', label: 'Contracts & Agreements' },
  { value: 'certifications-evidence', label: 'Certifications & Evidence' },
  { value: 'accountability', label: 'Accountability' },
];

export const GRC_PILLAR_OPTIONS = [
  { value: 'G', label: 'Governance' },
  { value: 'R', label: 'Risk' },
  { value: 'C', label: 'Compliance' },
  { value: 'Hybrid', label: 'Hybrid' },
];

export const DOMAIN_OPTIONS = [
  { value: 'data-privacy', label: 'Data & Privacy' },
  { value: 'ai-technology', label: 'AI & Technology' },
  { value: 'both', label: 'Both' },
];

const REGULATION_OPTIONS = [
  'GDPR', 'EU AI Act', 'CCPA', 'India DPDPA', 'ISO 27001', 'ISO 27701',
  'SOC 2', 'UK GDPR', 'LGPD', 'PIPEDA', 'PIPL', 'PDPA', 'APPI', 'PIPA',
];

interface UploadModalProps {
  open: boolean;
  onClose: () => void;
  onUpload: (file: File, meta: {
    document_name: string;
    category: string;
    grc_pillar: string;
    domain: string;
    jurisdiction_applicability?: string;
    regulations_mapped?: string;
    owner_contact_id?: number;
    last_review_date?: string;
    next_review_date?: string;
    expiry_date?: string;
    status?: string;
  }) => Promise<void>;
  contacts?: ContactEntry[];
  uploading?: boolean;
  initialCategory?: string;
}

export const UploadModal: React.FC<UploadModalProps> = ({
  open, onClose, onUpload, contacts = [], uploading = false, initialCategory = '',
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [pillar, setPillar] = useState('');
  const [domain, setDomain] = useState('');
  const [regulations, setRegulations] = useState<string[]>([]);
  const [ownerId, setOwnerId] = useState<number | undefined>();
  const [lastReview, setLastReview] = useState('');
  const [nextReview, setNextReview] = useState('');
  const [expiry, setExpiry] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const resetForm = () => {
    setFile(null); setName(''); setCategory(''); setPillar('');
    setDomain(''); setRegulations([]); setOwnerId(undefined);
    setLastReview(''); setNextReview(''); setExpiry('');
  };

  useEffect(() => {
    if (open) {
      setCategory(initialCategory || '');
    }
  }, [open, initialCategory]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files;
    if (dropped.length > 0) {
      setFile(dropped[0]);
      if (!name) setName(dropped[0].name.replace(/\.[^/.]+$/, ''));
    }
  }, [name]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) {
      setFile(f);
      if (!name) setName(f.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const toggleRegulation = (reg: string) => {
    setRegulations(prev =>
      prev.includes(reg) ? prev.filter(r => r !== reg) : [...prev, reg]
    );
  };

  const canSubmit = file && name.trim() && category && pillar && domain && !uploading;

  const handleSubmit = async () => {
    if (!canSubmit || !file) return;
    await onUpload(file, {
      document_name: name.trim(),
      category,
      grc_pillar: pillar,
      domain,
      regulations_mapped: regulations.length ? JSON.stringify(regulations) : undefined,
      owner_contact_id: ownerId,
      last_review_date: lastReview || undefined,
      next_review_date: nextReview || undefined,
      expiry_date: expiry || undefined,
    });
    resetForm();
    onClose();
  };

  if (!open) return null;

  const overlayStyle: React.CSSProperties = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
  };
  const modalStyle: React.CSSProperties = {
    background: 'var(--ds-surface)', borderRadius: 'var(--ds-radius-xl)', width: '100%', maxWidth: 560,
    maxHeight: '90vh', overflow: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.15)',
  };
  const headerStyle: React.CSSProperties = {
    padding: '20px 24px', borderBottom: '1px solid var(--ds-border)',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  };
  const bodyStyle: React.CSSProperties = { padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 };
  const fieldStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4 };
  const labelStyle: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: 'var(--ds-text-secondary)' };
  const inputStyle: React.CSSProperties = {
    border: '1px solid var(--ds-border-strong)', borderRadius: 'var(--ds-radius-md)', padding: '8px 12px', fontSize: 14, outline: 'none',
    width: '100%', boxSizing: 'border-box',
  };
  const selectStyle: React.CSSProperties = { ...inputStyle, background: 'var(--ds-surface)' };
  const reqStyle: React.CSSProperties = { color: 'var(--ds-danger-text)' };
  const btnPrimary: React.CSSProperties = {
    background: 'var(--primary-600)', color: 'var(--ds-text-on-dark)', border: 'none', borderRadius: 'var(--ds-radius-md)',
    padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
  };
  const btnSecondary: React.CSSProperties = {
    background: 'none', border: '1px solid var(--ds-border-strong)', borderRadius: 'var(--ds-radius-md)',
    padding: '10px 20px', fontSize: 14, cursor: 'pointer', color: 'var(--ds-text-secondary)',
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <div style={headerStyle}>
          <h2 style={{ margin: 0, fontFamily: 'var(--ds-font-display)', fontSize: 18, fontWeight: 700, letterSpacing: 'var(--ds-letter-tight)', color: 'var(--ds-text)' }}>Upload Document</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--ds-text-muted)' }}>&times;</button>
        </div>
        <div style={bodyStyle}>
          {/* Drop zone */}
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileRef.current?.click()}
            style={{
              border: `2px dashed ${dragOver ? 'var(--primary-600)' : 'var(--ds-border-strong)'}`,
              borderRadius: 12, padding: 24, textAlign: 'center', cursor: 'pointer',
              background: dragOver ? 'var(--primary-100)' : 'var(--ds-surface-2)', transition: 'all 0.2s',
            }}
          >
            <input ref={fileRef} type="file" hidden onChange={handleFileChange} />
            {file ? (
              <div style={{ fontSize: 14, color: 'var(--ds-text)', fontWeight: 500 }}>
                {file.name} ({(file.size / 1024).toFixed(1)} KB)
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 14, color: 'var(--ds-text-muted)' }}>Drag & drop a file here, or click to browse</div>
                <div style={{ fontSize: 12, color: 'var(--ds-text-faint)', marginTop: 4 }}>PDF, DOCX, XLSX, images accepted</div>
              </div>
            )}
          </div>

          {/* Document name */}
          <div style={fieldStyle}>
            <label style={labelStyle}>Document Name <span style={reqStyle}>*</span></label>
            <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. AI Usage Policy v2" />
          </div>

          {/* Category */}
          <div style={fieldStyle}>
            <label style={labelStyle}>Category <span style={reqStyle}>*</span></label>
            <select style={selectStyle} value={category} onChange={e => setCategory(e.target.value)}>
              <option value="">Select category...</option>
              {CATEGORY_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>

          {/* GRC Pillar + Domain row */}
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ ...fieldStyle, flex: 1 }}>
              <label style={labelStyle}>GRC Pillar <span style={reqStyle}>*</span></label>
              <select style={selectStyle} value={pillar} onChange={e => setPillar(e.target.value)}>
                <option value="">Select...</option>
                {GRC_PILLAR_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div style={{ ...fieldStyle, flex: 1 }}>
              <label style={labelStyle}>Domain <span style={reqStyle}>*</span></label>
              <select style={selectStyle} value={domain} onChange={e => setDomain(e.target.value)}>
                <option value="">Select...</option>
                {DOMAIN_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
          </div>

          {/* Regulations */}
          <div style={fieldStyle}>
            <label style={labelStyle}>Regulations Mapped</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {REGULATION_OPTIONS.map(reg => (
                <button
                  key={reg}
                  type="button"
                  onClick={() => toggleRegulation(reg)}
                  style={{
                    padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                    border: regulations.includes(reg) ? '1px solid var(--primary-600)' : '1px solid var(--ds-border-strong)',
                    background: regulations.includes(reg) ? 'var(--primary-100)' : 'var(--ds-surface)',
                    color: regulations.includes(reg) ? 'var(--primary-600)' : 'var(--ds-text-secondary)',
                  }}
                >{reg}</button>
              ))}
            </div>
          </div>

          {/* Owner */}
          {contacts.length > 0 && (
            <div style={fieldStyle}>
              <label style={labelStyle}>Owner</label>
              <select style={selectStyle} value={ownerId ?? ''} onChange={e => setOwnerId(e.target.value ? Number(e.target.value) : undefined)}>
                <option value="">Select owner...</option>
                {contacts.map(c => <option key={c.id} value={c.id}>{c.name} ({c.role})</option>)}
              </select>
            </div>
          )}

          {/* Dates row */}
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ ...fieldStyle, flex: 1 }}>
              <label style={labelStyle}>Last Review</label>
              <input style={inputStyle} type="date" value={lastReview} onChange={e => setLastReview(e.target.value)} />
            </div>
            <div style={{ ...fieldStyle, flex: 1 }}>
              <label style={labelStyle}>Next Review</label>
              <input style={inputStyle} type="date" value={nextReview} onChange={e => setNextReview(e.target.value)} />
            </div>
            <div style={{ ...fieldStyle, flex: 1 }}>
              <label style={labelStyle}>Expiry</label>
              <input style={inputStyle} type="date" value={expiry} onChange={e => setExpiry(e.target.value)} />
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 8 }}>
            <button style={btnSecondary} onClick={onClose}>Cancel</button>
            <button style={{ ...btnPrimary, opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? 'pointer' : 'not-allowed' }} disabled={!canSubmit} onClick={handleSubmit}>
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
